/**
 * organizationApi — get-full-organization/* body: { Version?: OrgVersion, PhanHe }.
 * Version phải là object VersionDTO (không string) — FE wrap tại save IndexedDB.
 */
import { IDepartmentViewModel, IPositionViewModel } from '@/types/employee';
import request from '../base-service';
import { InternalAxiosRequestConfig } from 'axios';
import { RequestDTO } from '@/types/requestDTO';
import type { OrgVersion } from '@/types/organizationIndexedDB';

const ORG_BASE_URL = '/v1';
const baseUrlApi = `${ORG_BASE_URL}/organization`;

/** Body cho các API get-full-organization (Version + PhanHe). */
export interface OrgInitRequestBody {
  Version?: OrgVersion | null;
  PhanHe?: string;
}

const defaultOrgBody: OrgInitRequestBody = { PhanHe: 'bsc' };

export const organizationApi = {

  // GET api/v1/organization/{id} — nhân viên nếu id chứa _nv, còn lại phòng ban
  find: (id: string | number) =>
    request.get(`${baseUrlApi}/${encodeURIComponent(String(id))}`, {
      authorization: true,
    } as InternalAxiosRequestConfig),


  /** Lấy riêng employees (response.data = AES string hoặc null). */
  getEmployees: (body: OrgInitRequestBody = defaultOrgBody) =>
    request.post<{ success: boolean; data: string | null; message?: string; version?: OrgVersion }>(
      `${baseUrlApi}/get-full-organization/employees`,
      body,
      { authorization: true } as InternalAxiosRequestConfig
    ),

  /** Lấy riêng departments. */
  getDepartments: (body: OrgInitRequestBody = defaultOrgBody) =>
    request.post<{ success: boolean; data: string | null; message?: string; version?: OrgVersion }>(
      `${baseUrlApi}/get-full-organization/departments`,
      body,
      { authorization: true } as InternalAxiosRequestConfig
    ),

  /** Lấy riêng positions. */
  getPositions: (body: OrgInitRequestBody = defaultOrgBody) =>
    request.post<{ success: boolean; data: string | null; message?: string; version?: OrgVersion }>(
      `${baseUrlApi}/get-full-organization/positions`,
      body,
      { authorization: true } as InternalAxiosRequestConfig
    ),

  /** Lấy units, roles, period, perspectives. */
  getRest: (body: OrgInitRequestBody = defaultOrgBody) =>
    request.post<{ success: boolean; data: string | null; message?: string; version?: OrgVersion }>(
      `${baseUrlApi}/get-full-organization/rest`,
      body,
      { authorization: true } as InternalAxiosRequestConfig
    ),

  clearAllCache: () =>
    request.get(`${baseUrlApi}/clearallcache`, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  /**
   * Lấy cached data theo module từ BE (BE đọc Redis → encrypt AES trả FE).
   * Trả { success, dataAvailable, module, version, data } — `data` là AES encrypted string.
   * FE phải `JSON.parse(CryptoService.decryptAES(res.data.data))` trước khi dùng.
   */
  getCachedData: (module: string) =>
    request.get<{ success: boolean; dataAvailable: boolean; module: string; version: string | null; data?: string }>(
      `${baseUrlApi}/cached-data/${module}`,
      { authorization: true } as InternalAxiosRequestConfig
    ),

  /** Lightweight version check — dùng polling khi SignalR không khả dụng. */
  getVersion: (body: { module: string; PhanHe?: string }) =>
    request.post<{ version: string; timestamp: string; module?: string; tenantId?: string }>(
      `${baseUrlApi}/version`,
      body,
      { authorization: true } as InternalAxiosRequestConfig
    ),

  departmentTreeExpand: (requestDTO: RequestDTO) =>
    request.post<IDepartmentViewModel[]>(`${baseUrlApi}/department-tree-expand`, requestDTO, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  positionTreeExpand: (requestDTO: RequestDTO) =>
    request.post<IPositionViewModel[]>(`${baseUrlApi}/position-tree-expand`, requestDTO, {
      authorization: true,
    } as InternalAxiosRequestConfig),
};
